import React from 'react';

interface ProfileFieldProps {
  label: string;
  value: any;
  priority: 'M' | 'GTF' | 'O';
  fieldKey?: string;
  isSelected?: boolean;
  onToggle?: (fieldKey: string) => void;
}

/**
 * Styled field component for displaying profile data with priority-based styling
 *
 * Priority levels:
 * - M (Mandatory): Green, bold, underlined - Critical for recommendations
 * - GTF (Good to Fill): Orange, bold - Significantly improves recommendations
 * - O (Optional): Gray - Nice to have but not essential
 *
 * Optional selection mode:
 * - When fieldKey, isSelected, and onToggle are provided, shows a checkbox
 * - Checkbox appears on hover or when selected
 * - Used for selecting which fields to show in rating modals
 */
const ProfileField: React.FC<ProfileFieldProps> = ({
  label,
  value,
  priority,
  fieldKey,
  isSelected = false,
  onToggle
}) => {
  // Styling based on priority
  const getStyle = () => {
    switch (priority) {
      case 'M':
        return {
          label: 'text-green-ink font-bold underline',
          badge: 'bg-green/15 text-green-ink border-green-ink',
          row: 'bg-green/10/30'
        };
      case 'GTF':
        return {
          label: 'text-orange-600 font-bold',
          badge: 'bg-orange-100 text-orange-600 border-orange-300',
          row: 'bg-orange-50/20'
        };
      case 'O':
        return {
          label: 'text-gray-700',
          badge: 'bg-gray-100 text-gray-600 border-gray-300',
          row: ''
        };
    }
  };

  const style = getStyle();
  const showCheckbox = fieldKey && onToggle;

  return (
    <div className={`relative flex flex-col py-1 px-2 hover:bg-gray-100 rounded transition-colors ${style.row} group`}>
      {/* Checkbox - appears on hover or when selected */}
      {showCheckbox && (
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggle(fieldKey)}
          className={`absolute top-1 right-1 w-4 h-4 cursor-pointer transition-opacity z-10 ${
            isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}
          title={isSelected ? "Remove from modal context" : "Show in modal context"}
        />
      )}

      <div className="flex items-center space-x-1 mb-0.5">
        <span className={`text-xs px-1 py-0 rounded border font-semibold ${style.badge}`}>
          {priority}
        </span>
        <span className={`${style.label} text-xs leading-tight`}>{label}</span>
      </div>
      <span className="text-sm text-gray-900 font-medium">{formatValue(value)}</span>
    </div>
  );
};

/**
 * Format field value for display
 */
function formatValue(value: any): string {
  if (value === null || value === undefined) {
    return '-';
  }

  if (typeof value === 'number') {
    // Check if it's a whole number or needs decimals
    return Number.isInteger(value) ? value.toString() : value.toFixed(1);
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  // Handle dates
  if (value instanceof Date) {
    return value.toLocaleDateString();
  }

  // Convert to string and clean up
  const strValue = String(value);

  // Replace underscores with spaces and capitalize
  if (strValue.includes('_')) {
    return strValue
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  return strValue;
}

export default ProfileField;
